import { Task } from '../redux/task/types'

/**
 * Processes tasks by filtering out archived tasks and adding computed properties
 */
export const processTasksData = (tasks: Task[]) => {
  return tasks
    .filter((task: Task) => !task.isArchived)
    .map((task) => {
      const dueDate = task.dueDate ? new Date(task.dueDate) : new Date()
      const currentDate = new Date()
      const timeDiff = dueDate.getTime() - currentDate.getTime()
      const daysOfDue = Math.ceil(timeDiff / (1000 * 3600 * 24))
      return { ...task, dueDate, daysOfDue }
    })
}

/**
 * Filters tasks based on importance criteria
 */
export const filterTasksByImportance = (
  tasks: Task[],
  importanceFilters: string[]
) => {
  if (importanceFilters.length === 0) return tasks
  
  return tasks.filter((task) => 
    importanceFilters.includes(task.importance ?? '')
  )
}

/**
 * Filters tasks based on status and importance criteria
 */
export const filterTasksByStatusAndImportance = (
  tasks: Task[],
  statusFilters: string[],
  importanceFilters: string[]
) => {
  return tasks.filter((task) => {
    const statusFilter = statusFilters.length === 0 || statusFilters.includes(task.status ?? '')
    const importanceFilter = importanceFilters.length === 0 || importanceFilters.includes(task.importance ?? '')
    
    return statusFilter && importanceFilter
  })
}

/**
 * Searches tasks by title
 */
export const searchTasksByTitle = (tasks: Task[], searchQuery: string) => {
  if (searchQuery.trim() === '') return tasks
  
  return tasks.filter((task) =>
    task.title.toLowerCase().includes(searchQuery.toLowerCase())
  )
}

/**
 * Filters tasks based on assignment criteria (operational manager, functional area, supervisor)
 */
export const filterTasksByAssignment = (
  tasks: Task[],
  operationalManagerFilters: string[],
  functionalAreaFilters: string[],
  supervisorFilters: string[]
) => {
  return tasks.filter((task) => {
    // If no assignment filters are selected, show all tasks
    if (operationalManagerFilters.length === 0 && 
        functionalAreaFilters.length === 0 && 
        supervisorFilters.length === 0) {
      return true
    }

    let matchesFilter = false

    // Check operational manager filter
    if (operationalManagerFilters.length > 0 && task.operationalManager) {
      const taskOperationalManagerIds = task.operationalManager.map(om => om.workdayId)
      matchesFilter = matchesFilter || operationalManagerFilters.some(filterId => 
        taskOperationalManagerIds.includes(filterId)
      )
    }

    // Check functional area filter
    if (functionalAreaFilters.length > 0 && task.functionalArea) {
      matchesFilter = matchesFilter || functionalAreaFilters.some(filterArea => 
        task.functionalArea?.includes(filterArea)
      )
    }

    // Check supervisor filter
    if (supervisorFilters.length > 0 && task.supervisor) {
      const taskSupervisorIds = task.supervisor.map(s => s.workdayId)
      matchesFilter = matchesFilter || supervisorFilters.some(filterId => 
        taskSupervisorIds.includes(filterId)
      )
    }

    return matchesFilter
  })
}

/**
 * Common task data processing pipeline for manage tasks
 */
export const processManageTasksData = (
  tasks: Task[],
  filters: {
    Importance: string[]
    OperationalManager: string[]
    FunctionalArea: string[]
    Supervisor: string[]
  },
  searchQuery: string
) => {
  const processedTasks = processTasksData(tasks)
  const importanceFilteredTasks = filterTasksByImportance(processedTasks, filters.Importance)
  const assignmentFilteredTasks = filterTasksByAssignment(
    importanceFilteredTasks, 
    filters.OperationalManager, 
    filters.FunctionalArea, 
    filters.Supervisor
  )
  return searchTasksByTitle(assignmentFilteredTasks, searchQuery)
}

/**
 * Common task data processing pipeline for view tasks
 */
export const processViewTasksData = (
  tasks: Task[],
  statusFilters: string[],
  importanceFilters: string[],
  searchQuery: string
) => {
  const processedTasks = processTasksData(tasks)
  const filteredTasks = filterTasksByStatusAndImportance(processedTasks, statusFilters, importanceFilters)
  return searchTasksByTitle(filteredTasks, searchQuery)
}
